import React, { useRef, useState, useEffect } from 'react';
import { Box, Skeleton, Tooltip } from '@mui/material';
import { ReactComponent as ArrowUp } from "../../assets/images/arrowUp.svg";
import { ReactComponent as ArrowDown } from "../../assets/images/arrowDown.svg";
import CountUp from 'react-countup';
import PropTypes from 'prop-types';

function OverviewChart({
  data = [],
  loading = false,
  textStyle: customTextStyle,
  containerStyle = {},
  statsContainerStyle = {},
  skeletonCount = 5,
}) {
  const defaultTextStyle = {
    color: '#405261',
    fontFamily: 'Inter',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: 'normal',
  };
  const textStyle = { ...defaultTextStyle, ...customTextStyle };
  const chartData = data || [];

  return (
    <Box className="Main-Header-Container" style={containerStyle}>
      <div
        className="Main-Chart-Container"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          fontFamily: 'Inter',
          ...statsContainerStyle,
        }}
      >
        {loading ? (
          Array.from({ length: skeletonCount }).map((_, index) => (
            <React.Fragment key={`skeleton-${index}`}>
              <div className="Main-chart" style={{ flex: 1, minWidth: 200 }}>
                <div style={{ width: '90%', padding: '18px 16px' }}>
                  <Skeleton variant="text" width="60%" height={20} />
                  <div className="chart-data" style={{ marginTop: '8px' }}>
                    <Skeleton variant="text" width={80} height={32} />
                    <div style={{ marginTop: '8px' }}>
                      <Skeleton variant="text" width={100} height={16} />
                      <Skeleton variant="text" width={60} height={12} style={{ marginTop: '4px' }} />
                    </div>
                  </div>
                </div>
              </div>
              {index < skeletonCount - 1 && (
                <div style={{ backgroundColor: '#e5e9ec', width: '1px', margin: '16px 0px' }} />
              )}
            </React.Fragment>
          ))
        ) : (
          chartData.map((item, index) => (
            <StatItem
              key={item.id || index}
              item={item}
              isLast={index === chartData.length - 1}
              textStyle={textStyle}
            />
          ))
        )}
      </div>
    </Box>
  );
}

const StatItem = React.memo(({ item, textStyle, isLast }) => {
  const showTrend = item.showTrend ?? true;
  const isText = item.isText ?? false;
  const displayPercentage = showTrend ? Math.abs(item.textPercentage || 0) : 0;
  const effectiveIsPositive = showTrend ? (item.textPercentage || 0) >= 0 : false;
  const rawNumber = item.number;
  const parsedNumber = Number(rawNumber);
  const isFiniteNumber = Number.isFinite(parsedNumber);
  const safeNumber = isFiniteNumber ? parsedNumber : 0;
  const trimmedText = String(rawNumber ?? '').trim();
  const isZeroTimeString =
    item.isTimeValue &&
    typeof rawNumber === 'string' &&
    /^((0+(\.0+)?)\s*s|0+\s*m(\s*0+\s*s)?)$/i.test(trimmedText);
  const isZeroTimeNumber = item.isTimeValue && isFiniteNumber && safeNumber === 0;
  const shouldShowDash =
    (isText && trimmedText === '0') ||
    (item.isTimeValue && (isZeroTimeNumber || isZeroTimeString)) ||
    (!isText && !item.isTimeValue && safeNumber === 0);

  // Ref and state for tooltip
  const textRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  // Check if text is truncated (has ellipsis)
  useEffect(() => {
    const checkOverflow = () => {
      if (isText && textRef.current) {
        const element = textRef.current;
        setIsOverflowing(element.scrollWidth > element.clientWidth);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);

    return () => window.removeEventListener('resize', checkOverflow);
  }, [isText, item.number]);

  const getDecimals = (num) => {
    if (num % 1 === 0) return 0;
    const decimalPart = num.toString().split('.')[1] || '';
    return decimalPart.length;
  };
  const decimals = getDecimals(safeNumber);

  const percentageClass = displayPercentage > 30 ? 'percentage-class' : 'percentage-class-error';

  return (
    <div className="Main-chart" style={{ flex: 1, minWidth: 200 }}>
      <div style={{ width: '100%', padding: '18px 16px' }}>
        <h4
          className="customer_main_Text"
          style={textStyle}
          aria-label={`Stat title: ${item.title}`}
        >
          {item.title}
        </h4>
        <div className="chart-data" role="status" aria-live="polite">
          <Tooltip
            title={shouldShowDash ? '--' : item.number || ''}
            arrow
            placement="top"
            disableHoverListener={!isText || !isOverflowing}
          >
            <p
              ref={textRef}
              className="number-text"
              style={{
                fontSize: isText ? '16px' : '28px',
                fontWeight: 500,
                color: '#061522',
                margin: 0,
                fontFamily: 'Inter',
                ...(isText && {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                }),
                cursor: isText && isOverflowing ? 'pointer' : 'default',
              }}
              aria-label={
                isText
                  ? `${item.title}: ${shouldShowDash ? '--' : item.number}`
                  : undefined
              }
            >
              {shouldShowDash ? (
                <span>--</span>
              ) : isText ? (
                <span>{item.number}</span>
              ) : item.isTimeValue ? (
                item.number
              ) : (
                <>
                  <CountUp
                    duration={2.5}
                    end={safeNumber}
                    separator=","
                    decimals={decimals}
                  />
                  {item.isPercentage && '%'}
                </>
              )}
            </p>
          </Tooltip>
          <div>
            {item.textPercentage !== null && (
              <p
                className={percentageClass}
                style={{
                  ...textStyle,
                  fontSize: '14px',
                  fontWeight: 500,
                  margin: 0,
                  marginBottom: '4px',
                  display: 'flex',
                  color: effectiveIsPositive ? '#148DFD' : '#FF4D4F',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                {displayPercentage === 0 ? (
                  '--'
                ) : (
                  <CountUp duration={1.5} end={displayPercentage} />
                )}
                %
                {effectiveIsPositive ? (
                  <ArrowUp />
                ) : (
                  <ArrowDown />
                )}
              </p>
            )}
            <p
              className="week-class"
              style={{
                ...textStyle,
                color: '#6B7280',
                fontSize: '12px',
                margin: 0,
                minHeight: item.textPercentage === null ? '0' : 'auto',
                visibility: item.textPercentage === null ? 'hidden' : 'visible',
              }}
            >
              {item.value}
            </p>
          </div>
        </div>
      </div>
      {!isLast && <div style={{ backgroundColor: '#e5e9ec', width: '1px', margin: '16px 0px' }} />}
    </div>
  );
});

StatItem.propTypes = {
  item: PropTypes.shape({
    title: PropTypes.string.isRequired,
    number: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    textPercentage: PropTypes.number,
    value: PropTypes.string,
    showTrend: PropTypes.bool,
    isText: PropTypes.bool,
    isTimeValue: PropTypes.bool,
    isPercentage: PropTypes.bool,
  }).isRequired,
  textStyle: PropTypes.object,
  isLast: PropTypes.bool,
};

StatItem.displayName = 'StatItem';

OverviewChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})),
  loading: PropTypes.bool,
};

export default React.memo(OverviewChart);